import { useCallback, useRef, useSyncExternalStore } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  getIdToken,
  getSessionGeneration,
  getSessionRevision,
  subscribeSession,
} from '../../providers/cognito';
import { captureSubject } from '../../utils/capture-session';

/** Client isolation only. Integration routes verify signature, principal and exact role. */
export function integrationPrincipal(token: string | null) {
  const actorId = captureSubject(token);
  if (!actorId || !token) return undefined;
  try {
    const { tenantId } = jwtDecode<{ tenantId?: unknown }>(token);
    if (typeof tenantId !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(tenantId)) return undefined;
    return { actorId, tenantId };
  } catch { return undefined; }
}
/** Does not call the instructor-only capture-context endpoint. */
export function useIntegrationSession() {
  const revoked = useRef<string | null>(null);
  const subscribe = useCallback((notify: () => void) => {
    const timer = window.setInterval(() => notify(), 1000);
    const unsubscribeSession = subscribeSession(notify);
    const storage = (event: StorageEvent) => {
      if (!event.key || event.key.includes('CognitoIdentityServiceProvider')) { revoked.current = getIdToken(); notify(); }
    };
    window.addEventListener('storage', storage);
    return () => {
      window.clearInterval(timer);
      unsubscribeSession();
      window.removeEventListener('storage', storage);
    };
  }, []);
  const snapshot = useCallback(() => {
    const token = getIdToken();
    const activeToken = token !== revoked.current && integrationPrincipal(token) ? token : null;
    return JSON.stringify([
      getSessionRevision(),
      getSessionGeneration(),
      activeToken,
    ]);
  }, []);
  const sessionKey = useSyncExternalStore(
    subscribe,
    snapshot,
    () => JSON.stringify([0, 0, null]),
  );
  const [revision, generation, token] = JSON.parse(sessionKey) as [
    number,
    number,
    string | null,
  ];
  return {
    token,
    context: integrationPrincipal(token),
    revision,
    generation,
    sessionKey,
    isCurrent: () => !!token && snapshot() === sessionKey,
  };
}
